import React from 'react';

import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const getTones = theme => {
  const { brand, common, context } = theme.palette;
  const contextRamp = context?.current === 'institutions' ? brand.blue : brand.green;

  return {
    normal: {
      text: brand.grey[700],
      standard: brand.grey[200],
      border: brand.grey[400],
      contained: brand.grey[700],
      containedText: common.white,
    },
    primary: {
      text: contextRamp[400],
      standard: contextRamp[100],
      border: contextRamp[200],
      contained: contextRamp[400],
      containedText: common.white,
    },
    secondary: {
      text: contextRamp[500],
      standard: contextRamp[200],
      border: contextRamp[300],
      contained: contextRamp[500],
      containedText: common.white,
    },
    info: {
      text: brand.grey[600],
      standard: brand.grey[200],
      border: brand.grey[400],
      contained: brand.grey[600],
      containedText: common.white,
    },
    success: {
      text: brand.green[400],
      standard: brand.green[100],
      border: brand.green[200],
      contained: brand.green[400],
      containedText: common.white,
    },
    warning: {
      text: brand.yellow[400],
      standard: brand.yellow[100],
      border: brand.yellow[200],
      contained: brand.yellow[300],
      containedText: common.white,
    },
    error: {
      text: brand.red[500],
      standard: brand.red[100],
      border: brand.red[200],
      contained: brand.red[400],
      containedText: common.white,
    },
  };
};

const useStyles = makeStyles(theme => {
  const tones = getTones(theme);

  return {
    root: {
      minWidth: 20,
      maxWidth: '100%',
      height: 20,
      fontFamily: theme.typography.fontFamily,
      alignItems: 'center',
      borderRadius: theme.spacing(1),
      display: 'inline-flex',
      flexGrow: 0,
      gap: theme.spacing(1),
      whiteSpace: 'nowrap',
      cursor: 'default',
      flexShrink: 0,
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 600,
      justifyContent: 'center',
      letterSpacing: 0.5,
      lineHeight: 1,
      padding: theme.spacing(0.5, 1.5),
      textTransform: 'uppercase',
    },
    xs: {
      fontSize: theme.typography.pxToRem(10),
      fontWeight: theme.typography.fontWeightRegular,
    },
    sm: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 600,
    },
    md: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 600,
    },
    lg: {
      minWidth: 40,
      height: 40,
      fontSize: theme.typography.pxToRem(22),
      fontWeight: theme.typography.fontWeightBold,
    },
    tone: props => {
      const tone = tones[props.color];

      if (!tone) return {};

      if (props.variant === 'contained') {
        return { color: tone.containedText, backgroundColor: tone.contained };
      }

      return {
        color: tone.text,
        backgroundColor: tone.standard,
        boxShadow: props.variant === 'outlined' ? `inset 0 0 0 1px ${tone.border}` : 'none',
      };
    },
    clickable: {
      cursor: 'pointer',
      '&:hover': { opacity: 0.85 },
    },
  };
});

function Label({ className, size = 'sm', color, variant, clickable, children, ...rest }) {
  const classes = useStyles({ color, variant });

  return (
    <span
      className={clsx(
        classes.root,
        classes.tone,
        { [classes[size]]: size },
        className,
        clickable && classes.clickable,
      )}
      {...rest}>
      {children}
    </span>
  );
}

Label.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['standard', 'outlined', 'contained']),
  color: PropTypes.oneOf(['normal', 'primary', 'secondary', 'error', 'info', 'warning', 'success']),
  clickable: PropTypes.bool,
};

Label.defaultProps = {
  className: '',
  color: 'normal',
  variant: 'standard',
  clickable: false,
};

export default Label;
